import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, MapPin, User, Euro, Phone, Mail, Home as HomeIcon,
    FileDown, Ruler, ThermometerSun, StickyNote, Wrench,
} from 'lucide-react';

const formatEUR = (amount) => {
    if (!amount) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
};

const PROP_TYPES = { apartment: 'Appartement', house: 'Maison', land: 'Terrain', other: 'Autre' };
const HEATING = {
    chauffage_sol: 'Chauffage au sol', pompe_chaleur: 'Pompe à chaleur', gaz: 'Gaz',
    electrique: 'Électrique', fioul: 'Fioul', bois: 'Bois / Granulés',
    clim_reversible: 'Clim réversible', collectif: 'Collectif', autre: 'Autre',
};
const HOT_WATER = {
    ballon: 'Ballon électrique', chaudiere: 'Chaudière', instantane: 'Instantané',
    solaire: 'Solaire', pompe_chaleur: 'Pompe à chaleur',
};
const WINDOWS = { simple: 'Simple vitrage', double: 'Double vitrage', triple: 'Triple vitrage' };

const CHIPS = [
    ['elevator', 'Ascenseur'], ['parking', 'Parking'], ['garage', 'Garage'],
    ['cellar', 'Cave'], ['balcony', 'Balcon'], ['terrace', 'Terrasse'],
    ['garden', 'Jardin'], ['pool', 'Piscine'],
    ['air_conditioning', 'Climatisation'], ['fiber_internet', 'Fibre'],
];

export default function EstimationPreviewPanel({ estimation, onClose, onDownloadPdf }) {
    if (!estimation) return null;
    const e = estimation;
    const p = e.prospect || {};
    const prop = e.property || {};
    const eq = prop.equipment || {};
    const rooms = prop.rooms_detail || [];
    const totalRooms = rooms.reduce((s, r) => s + (Number(r.surface_m2) || 0), 0);
    const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Lead sans nom';

    return (
        <AnimatePresence>
            {e && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
                        onClick={onClose}
                        data-testid="estim-panel-overlay"
                    />
                    <motion.div
                        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
                        className="fixed top-0 right-0 h-full w-full max-w-2xl bg-slate-950 border-l border-white/10 z-50 overflow-y-auto"
                        data-testid="estim-preview-panel"
                    >
                        {/* Header sticky */}
                        <div className="sticky top-0 bg-slate-950/95 backdrop-blur-md border-b border-white/10 px-5 py-3.5 flex items-center justify-between z-10">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#681793]/20 to-[#FF914D]/20 flex items-center justify-center shrink-0">
                                    <User className="w-4 h-4 text-[#FF914D]" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-foreground truncate">{fullName}</p>
                                    <p className="text-[10px] text-muted-foreground truncate">
                                        {prop.address || 'Aucune adresse'}{prop.city ? ` · ${prop.city}` : ''}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {onDownloadPdf && (
                                    <button
                                        onClick={() => onDownloadPdf(e)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF914D]/10 hover:bg-[#FF914D]/20 border border-[#FF914D]/20 text-[#FF914D] text-xs font-bold transition-colors"
                                        data-testid="estim-panel-pdf-btn"
                                    >
                                        <FileDown className="w-3.5 h-3.5" /> PDF
                                    </button>
                                )}
                                <button
                                    onClick={onClose}
                                    className="p-1.5 rounded-lg hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors"
                                    data-testid="estim-panel-close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <div className="p-5 space-y-5">
                            {/* Fourchette prix (héro) */}
                            {(e.estimation?.low || e.estimation?.mid || e.estimation?.high) && (
                                <div className="rounded-2xl bg-gradient-to-br from-[#FF914D]/10 to-[#681793]/10 border border-[#FF914D]/20 p-4">
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-3 text-center">Fourchette d'estimation</p>
                                    <div className="grid grid-cols-3 gap-2 text-center">
                                        <div>
                                            <p className="text-[10px] text-muted-foreground">Bas</p>
                                            <p className="text-sm font-bold text-foreground">{formatEUR(e.estimation?.low)}</p>
                                        </div>
                                        <div className="border-x border-white/10">
                                            <p className="text-[10px] text-[#FF914D] font-bold">Estimation</p>
                                            <p className="text-2xl font-black text-[#FF914D]">{formatEUR(e.estimation?.mid)}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-muted-foreground">Haut</p>
                                            <p className="text-sm font-bold text-foreground">{formatEUR(e.estimation?.high)}</p>
                                        </div>
                                    </div>
                                    {e.seller_asking_price && (
                                        <p className="text-[10px] text-center text-muted-foreground mt-3 pt-3 border-t border-white/5">
                                            Prix demandé par le vendeur : <span className="text-foreground font-bold">{formatEUR(e.seller_asking_price)}</span>
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Vendeur + Bien (2 colonnes) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="rounded-xl bg-card border border-border p-3">
                                    <p className="text-[10px] uppercase tracking-wider text-[#FF914D] font-bold mb-2">Vendeur</p>
                                    <p className="text-sm font-bold text-foreground">{fullName}</p>
                                    {p.email && <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1"><Mail className="w-3 h-3" />{p.email}</p>}
                                    {p.phone && <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1"><Phone className="w-3 h-3" />{p.phone}</p>}
                                </div>
                                <div className="rounded-xl bg-card border border-border p-3">
                                    <p className="text-[10px] uppercase tracking-wider text-[#FF914D] font-bold mb-2">Bien estimé</p>
                                    <p className="text-sm font-bold text-foreground">{PROP_TYPES[prop.property_type] || 'Bien'}</p>
                                    {prop.address && <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1"><MapPin className="w-3 h-3" />{prop.address}</p>}
                                    {(prop.postal_code || prop.city) && <p className="text-xs text-muted-foreground ml-4">{prop.postal_code} {prop.city}</p>}
                                </div>
                            </div>

                            {/* Caractéristiques en grille */}
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2 flex items-center gap-1.5">
                                    <HomeIcon className="w-3 h-3 text-[#FF914D]" /> Caractéristiques
                                </p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {[
                                        ['Surface', prop.surface_m2 ? `${prop.surface_m2} m²` : null],
                                        ['Pièces', prop.rooms],
                                        ['Étage', prop.floor != null ? `${prop.floor}${prop.total_floors ? ` / ${prop.total_floors}` : ''}` : null],
                                        ['Année', prop.year_built],
                                        ['DPE', prop.dpe],
                                        ['GES', eq.ges],
                                    ].filter(([, v]) => v).map(([k, v]) => (
                                        <div key={k} className="rounded-lg bg-white/[0.02] border border-white/5 px-2.5 py-2">
                                            <p className="text-[9px] uppercase text-muted-foreground">{k}</p>
                                            <p className="text-xs font-bold text-foreground">{v}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Équipements */}
                            {(eq.heating_type || eq.windows || eq.exposure || eq.hot_water) && (
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2 flex items-center gap-1.5">
                                        <ThermometerSun className="w-3 h-3 text-[#FF914D]" /> Équipements
                                    </p>
                                    <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3 space-y-1.5 text-xs">
                                        {eq.heating_type && (
                                            <p><span className="text-muted-foreground">Chauffage : </span><span className="text-foreground">{HEATING[eq.heating_type] || eq.heating_type}</span>{eq.heating_details && <span className="text-muted-foreground"> · {eq.heating_details}</span>}</p>
                                        )}
                                        {eq.hot_water && <p><span className="text-muted-foreground">Eau chaude : </span><span className="text-foreground">{HOT_WATER[eq.hot_water] || eq.hot_water}</span></p>}
                                        {eq.windows && <p><span className="text-muted-foreground">Fenêtres : </span><span className="text-foreground">{WINDOWS[eq.windows]}</span></p>}
                                        {eq.exposure && <p><span className="text-muted-foreground">Exposition : </span><span className="text-foreground">{eq.exposure}</span></p>}
                                    </div>
                                </div>
                            )}

                            {/* Chips annexes */}
                            {CHIPS.some(([k]) => eq[k]) && (
                                <div className="flex flex-wrap gap-1.5">
                                    {CHIPS.filter(([k]) => eq[k]).map(([k, label]) => (
                                        <span key={k} className="px-2.5 py-1 rounded-full bg-[#FF914D]/10 border border-[#FF914D]/20 text-[#FF914D] text-[10px] font-bold">
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Pièces */}
                            {rooms.length > 0 && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1.5">
                                            <Ruler className="w-3 h-3 text-[#FF914D]" /> Relevé pièces ({rooms.length})
                                        </p>
                                        {totalRooms > 0 && (
                                            <span className="text-[10px] text-[#FF914D] font-bold">Total {totalRooms.toFixed(2)} m²</span>
                                        )}
                                    </div>
                                    <div className="rounded-xl bg-white/[0.02] border border-white/5 divide-y divide-white/5">
                                        {rooms.map((r, i) => (
                                            <div key={i} className="px-3 py-2 flex items-center justify-between gap-2 text-xs">
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-bold text-foreground truncate">{r.name || `Pièce ${i + 1}`}</p>
                                                    {r.notes && <p className="text-[10px] text-muted-foreground truncate">{r.notes}</p>}
                                                </div>
                                                <div className="text-right shrink-0">
                                                    {r.length_m && r.width_m && (
                                                        <p className="text-[10px] text-muted-foreground">
                                                            {r.length_m} × {r.width_m}{r.height_m ? ` × ${r.height_m}` : ''} m
                                                        </p>
                                                    )}
                                                    {r.surface_m2 && <p className="text-[#FF914D] font-bold">{r.surface_m2} m²</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Travaux */}
                            {(eq.works_done || eq.works_needed) && (
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2 flex items-center gap-1.5">
                                        <Wrench className="w-3 h-3 text-[#FF914D]" /> Travaux
                                    </p>
                                    <div className="space-y-2">
                                        {eq.works_done && (
                                            <div className="rounded-lg bg-green-500/5 border border-green-500/10 p-2.5">
                                                <p className="text-[10px] uppercase font-bold text-green-400 mb-1">Récents</p>
                                                <p className="text-xs text-foreground whitespace-pre-line">{eq.works_done}</p>
                                            </div>
                                        )}
                                        {eq.works_needed && (
                                            <div className="rounded-lg bg-red-500/5 border border-red-500/10 p-2.5">
                                                <p className="text-[10px] uppercase font-bold text-red-400 mb-1">À prévoir</p>
                                                <p className="text-xs text-foreground whitespace-pre-line">{eq.works_needed}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Argumentaire */}
                            {e.estimation?.arguments && (
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2 flex items-center gap-1.5">
                                        <Euro className="w-3 h-3 text-[#FF914D]" /> Argumentaire commercial
                                    </p>
                                    <div className="rounded-xl bg-card border border-border p-3 text-xs text-foreground whitespace-pre-line">
                                        {e.estimation.arguments}
                                    </div>
                                </div>
                            )}

                            {/* Notes de visite (interne) */}
                            {prop.visit_notes && (
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2 flex items-center gap-1.5">
                                        <StickyNote className="w-3 h-3 text-[#FF914D]" /> Notes de visite (interne)
                                    </p>
                                    <div className="rounded-xl bg-white/[0.02] border border-dashed border-white/10 p-3 text-xs text-muted-foreground whitespace-pre-line leading-relaxed">
                                        {prop.visit_notes}
                                    </div>
                                </div>
                            )}

                            {e.notes && (
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Contexte lead</p>
                                    <p className="text-xs text-muted-foreground italic">{e.notes}</p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
